import React from 'react';
import { Archive, CheckCircle, FileEdit, PauseCircle, XCircle } from 'lucide-react';

const I18N_PREFIX = 'modules.article.pages.catalog_status';

export const ARTICLE_STATUS_VALUES = ['actif', 'inactif', 'brouillon', 'suspendu', 'archive'];
export const SERVICE_STATUS_VALUES = ['active', 'inactive', 'draft', 'suspended', 'archived'];

const ARTICLE_DEFS = [
  { value: 'actif', labelKey: 'active', Icon: CheckCircle, iconClass: 'text-green-400', style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'inactif', labelKey: 'inactive', Icon: XCircle, iconClass: 'text-red-400', style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'brouillon', labelKey: 'draft', Icon: FileEdit, iconClass: 'text-amber-300', style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  { value: 'suspendu', labelKey: 'suspended', Icon: PauseCircle, iconClass: 'text-orange-300', style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  { value: 'archive', labelKey: 'archived', Icon: Archive, iconClass: 'text-slate-300', style: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
];

const SERVICE_DEFS = [
  { value: 'active', labelKey: 'active', Icon: CheckCircle, iconClass: 'text-green-400', style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 'inactive', labelKey: 'inactive', Icon: XCircle, iconClass: 'text-red-400', style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'draft', labelKey: 'draft', Icon: FileEdit, iconClass: 'text-amber-300', style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  { value: 'suspended', labelKey: 'suspended', Icon: PauseCircle, iconClass: 'text-orange-300', style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
  { value: 'archived', labelKey: 'archived', Icon: Archive, iconClass: 'text-slate-300', style: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
];

const STATUS_ALIASES = {
  actif: 'active',
  active: 'active',
  inactif: 'inactive',
  inactive: 'inactive',
  brouillon: 'draft',
  draft: 'draft',
  suspendu: 'suspended',
  suspended: 'suspended',
  archive: 'archived',
  archived: 'archived',
};

const STYLE_BY_CANONICAL = Object.fromEntries(
  [...ARTICLE_DEFS, ...SERVICE_DEFS].map((d) => [d.labelKey, d.style]),
);

export function normalizeCatalogStatus(status) {
  if (status === true) return 'active';
  if (status === false) return 'inactive';
  return String(status ?? '').trim().toLowerCase();
}

export function getCatalogStatusCanonical(status) {
  const normalized = normalizeCatalogStatus(status);
  return STATUS_ALIASES[normalized] || normalized || 'inactive';
}

export function getCatalogStatusLabel(status, t) {
  const canonical = getCatalogStatusCanonical(status);
  return t(`${I18N_PREFIX}.${canonical}`, { defaultValue: status || '—' });
}

export function getCatalogStatusBadgeStyle(status) {
  const canonical = getCatalogStatusCanonical(status);
  return STYLE_BY_CANONICAL[canonical] || STYLE_BY_CANONICAL.inactive;
}

function mapDefsToSelectOptions(defs, t, { withIcons = false, isDefault = true } = {}) {
  return defs.map((d) => ({
    value: d.value,
    label: t(`${I18N_PREFIX}.${d.labelKey}`),
    ...(isDefault ? { isDefault: true } : {}),
    ...(withIcons ? { icon: React.createElement(d.Icon, { size: 14, className: d.iconClass }) } : {}),
  }));
}

function mapDefsToBadgeOptions(defs, t) {
  return defs.map((d) => ({
    value: d.value,
    label: t(`${I18N_PREFIX}.${d.labelKey}`),
    icon: d.Icon,
    style: d.style,
  }));
}

export function buildArticleStatusSelectOptions(t, options = {}) {
  return mapDefsToSelectOptions(ARTICLE_DEFS, t, options);
}

export function buildServiceStatusSelectOptions(t, options = {}) {
  return mapDefsToSelectOptions(SERVICE_DEFS, t, options);
}

export function buildArticleStatusBadgeOptions(t) {
  return mapDefsToBadgeOptions(ARTICLE_DEFS, t);
}

export function buildServiceStatusBadgeOptions(t) {
  return mapDefsToBadgeOptions(SERVICE_DEFS, t);
}

/** Arabic labels for TableColumnHeaderFilter status dropdowns (internal values unchanged). */
export function buildArticleStatusColumnFilterOptions(t) {
  return buildArticleStatusSelectOptions(t).map(({ value, label }) => ({ value, label }));
}

export function buildServiceStatusColumnFilterOptions(t) {
  return buildServiceStatusSelectOptions(t).map(({ value, label }) => ({ value, label }));
}

export function categoryStatusFromRecord(record) {
  if (record?.status) return normalizeCatalogStatus(record.status);
  return record?.active ? 'active' : 'inactive';
}

export function categoryActiveFromStatus(status) {
  return getCatalogStatusCanonical(status) === 'active';
}
